// =============================================================================
// Importação em volume — o que torna o orçamento de 2027 viável
//
//   "Permitir entrada manual segura de orçamento, inclusive em volume
//    compatível com o planejamento anual." (AF-01 §2)
//
// A tela VALIDA ANTES DE GRAVAR. A RPC também separa o que entrou do que não
// casou, mas descobrir isso depois de escrever significa que o Financeiro já
// tem linhas metade certas no banco e precisa caçar quais. Validando antes, ele
// corrige a planilha e importa uma vez.
// =============================================================================

import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, FalhaApi } from "../lib/api.ts";
import { moeda } from "../lib/formato.ts";
import { paraCentavos } from "../lib/dinheiro.ts";
import { planejarAtualizacaoEmMassa } from "../lib/atualizacaoOrcamento.ts";
import {
  lerPlanilha, lerCompetencia, exemploColado, modeloCsv, decodificarArquivo, type LinhaPlanilha,
} from "../lib/planilha.ts";
import {
  lerOrcamentoXlsx, type LeituraOrcamentoXlsx, type LinhaOrcamentoXlsx,
} from "../lib/xlsxOrcamento.ts";
import { Painel, Pilula, Vazio } from "../componentes/ui.tsx";

type Cadastros = {
  centros: { id: string; codigo: string; nome: string }[];
  categorias: { id: string; codigo: string | null; nome: string }[];
  exercicios: { ano: number; status: string }[];
};

type Resultado = {
  aceitas: number;
  rejeitadas: number;
  sem_correspondencia: number;
  /** Aceitas que caíram numa coordenada que já tinha orçamento (migration 042). */
  repetidas?: number;
  detalhes: { linha: unknown; resultado: string; motivo: string }[];
  avisos?: {
    indice: number;
    aviso: string;
    coordenada_ocupada?: "banco" | "lote";
    linha?: { centro_custo_codigo?: string; categoria_nome?: string; competencia?: string };
  }[];
  ignoradas_ocupadas?: number;
  ocupadas?: {
    centro_custo_codigo?: string;
    categoria_codigo?: string | null;
    categoria_nome?: string | null;
    competencia?: string;
  }[];
};

type LinhaEntrada = LinhaPlanilha & Partial<Pick<
  LinhaOrcamentoXlsx,
  "origem_xlsx" | "linha_xlsx" | "categoria_codigo"
>>;

type LinhaExistente = {
  linha_id: string;
  centro_custo_id: string;
  centro_custo_codigo: string;
  categoria_id: string;
  categoria_codigo: string | null;
  categoria_nome: string;
  competencia: string;
  valor_centavos: string;
  subcategorias: { id: string; nome: string; valor_centavos: string }[];
};

type Conferida = {
  origem: LinhaEntrada;
  numero: number | null;
  centroCustoCodigo: string | null;
  categoriaId: string | null;
  categoriaCodigo: string | null;
  categoriaNome: string | null;
  subcategoria: string;
  tipo: string;
  centavos: string | null;
  competencia: string | null;
  problemas: string[];
  aviso?: string;
};

type LinhaMontada = {
  centro_custo_codigo: string | null;
  categoria_id: string | null;
  categoria_codigo: string | null;
  categoria_nome: string | null;
  tipo: string;
  competencia: string | null;
  valor_centavos: string;
  subcategorias?: { nome: string; valor_centavos: string }[];
};

const TIPOS = ["pontual", "recorrente", "investimento"];

/**
 * As linhas que vão para a RPC.
 *
 * Sem subcategoria, cada linha da planilha é uma linha orçamentária, como
 * sempre foi. COM subcategoria (18/09), as linhas do mesmo centro de custo +
 * categoria + tipo + mês viram UMA linha orçamentária, e cada uma delas uma
 * subcategoria — a linha vale a soma, que é o que a RPC exige (022). É a forma
 * da aba MOVIMENTACOES, e o que dá ao motor o nome contra o qual casar a
 * descrição do lançamento (§3.5). Mesmo nome repetido no grupo soma.
 */
function montarLinhas(boas: Conferida[]): LinhaMontada[] {
  const grupos = new Map<string, {
    base: Omit<LinhaMontada, "valor_centavos" | "subcategorias">;
    subs: Map<string, { nome: string; centavos: bigint }>;
    semSub: bigint;
  }>();
  for (const c of boas) {
    const base = {
      centro_custo_codigo: c.centroCustoCodigo,
      categoria_id: c.categoriaId,
      categoria_codigo: c.categoriaCodigo,
      categoria_nome: c.categoriaNome,
      tipo: c.tipo,
      competencia: c.competencia,
    };
    // A coordenada do orçamento não inclui tipo. Agrupar também as linhas sem
    // subcategoria evita que um CSV repetido crie duas linhas no mesmo mês.
    const chave = [c.centroCustoCodigo, c.categoriaId, c.competencia].join("|");
    let g = grupos.get(chave);
    if (!g) grupos.set(chave, (g = { base, subs: new Map(), semSub: 0n }));
    if (!c.subcategoria) {
      g.semSub += BigInt(c.centavos ?? "0");
      continue;
    }
    const k = c.subcategoria.toLowerCase();
    const atual = g.subs.get(k);
    g.subs.set(k, {
      nome: atual?.nome ?? c.subcategoria,
      centavos: (atual?.centavos ?? 0n) + BigInt(c.centavos ?? "0"),
    });
  }
  return [...grupos.values()].map(({ base, subs, semSub }) => {
    const lista = [...subs.values()];
    if (lista.length > 0 && semSub > 0n) lista.unshift({ nome: "Sem subcategoria", centavos: semSub });
    const total = lista.length > 0
      ? lista.reduce((s, x) => s + x.centavos, 0n)
      : semSub;
    return {
      ...base,
      valor_centavos: total.toString(),
      ...(lista.length > 0
        ? { subcategorias: lista.map((x) => ({ nome: x.nome, valor_centavos: x.centavos.toString() })) }
        : {}),
    };
  });
}

const chaveCoordenada = (cc: string | null, categoriaId: string | null, competencia: string | null) =>
  `${cc ?? ""}|${categoriaId ?? ""}|${competencia ?? ""}`;

/** Baixa o modelo. O BOM faz o Excel ler o arquivo como UTF-8 e não estragar os acentos. */
function baixarModelo(ano: number) {
  const blob = new Blob(["\uFEFF" + modeloCsv(ano)], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `modelo_orcamento_${ano}.csv`;
  a.click();
  // Revogar no mesmo tick cancela o download em alguns navegadores.
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

export function ImportarOrcamento() {
  const qc = useQueryClient();
  const [texto, setTexto] = useState("");
  const [xlsx, setXlsx] = useState<LeituraOrcamentoXlsx | null>(null);
  const [lendoArquivo, setLendoArquivo] = useState(false);
  const [erroArquivo, setErroArquivo] = useState<string | null>(null);
  const [exercicio, setExercicio] = useState<number>(new Date().getFullYear() + 1);
  const [resultado, setResultado] = useState<Resultado | null>(null);
  const [modo, setModo] = useState<"novos" | "atualizar">("novos");
  const [justificativa, setJustificativa] = useState("");
  const [resultadoAtualizacao, setResultadoAtualizacao] = useState<{ alteradas: number; aviso?: string | null } | null>(null);

  const cadastros = useQuery({
    queryKey: ["cadastros"],
    queryFn: () => api.get<Cadastros>("/api/cadastros"),
  });

  const leituraTexto = useMemo(() => (texto.trim() ? lerPlanilha(texto) : null), [texto]);
  const linhasLidas: LinhaEntrada[] = xlsx?.linhas ?? leituraTexto?.linhas ?? [];

  // O plano de contas real tem nomes repetidos em ramos diferentes — "Plano
  // Empresarial" aparece duas vezes (docs/PLANO_DE_CONTAS.md §4). O .xlsx traz
  // o código e resolve a ambiguidade; no CSV antigo, nome ambíguo é bloqueado.
  const nomesAmbiguos = useMemo(() => {
    const n = new Map<string, number>();
    for (const c of cadastros.data?.categorias ?? []) {
      const k = c.nome.trim().toLowerCase();
      n.set(k, (n.get(k) ?? 0) + 1);
    }
    return new Set([...n.entries()].filter(([, q]) => q > 1).map(([k]) => k));
  }, [cadastros.data]);

  const conferidas: Conferida[] = useMemo(() => {
    if (!linhasLidas.length || !cadastros.data) return [];
    const centros = new Set(cadastros.data.centros.map((c) => c.codigo));
    const categoriasPorNome = new Map(
      cadastros.data.categorias.map((c) => [c.nome.trim().toLowerCase(), c]),
    );
    const categoriasPorCodigo = new Map(
      cadastros.data.categorias.filter((c) => c.codigo).map((c) => [c.codigo!, c]),
    );

    return linhasLidas.map((l, i) => {
      const problemas: string[] = [];

      const cc = l.centro_custo.trim();
      if (!cc) problemas.push("centro de custo vazio");
      else if (!centros.has(cc)) problemas.push(`centro de custo ${cc} não existe`);

      const catBruta = l.categoria.trim();
      const catChave = catBruta.toLowerCase();
      const codigoBruto = l.categoria_codigo?.trim() ?? "";
      const porCodigo = codigoBruto ? categoriasPorCodigo.get(codigoBruto) : undefined;
      const porNome = catBruta ? categoriasPorNome.get(catChave) : undefined;
      const categoria = porCodigo ?? porNome;
      if (codigoBruto && !porCodigo) problemas.push(`categoria ${codigoBruto} não existe no sistema`);
      else if (!catBruta) problemas.push(codigoBruto ? `categoria ${codigoBruto} sem nome no PLANO_CONTAS` : "categoria vazia");
      else if (!porNome) problemas.push(`categoria "${catBruta}" não existe`);
      else if (porCodigo && porCodigo.id !== porNome.id) {
        problemas.push(`código ${codigoBruto} e nome "${catBruta}" apontam para categorias diferentes`);
      }
      else if (!codigoBruto && nomesAmbiguos.has(catChave)) {
        problemas.push(`há mais de uma categoria chamada "${catBruta}"; informe o código no .xlsx`);
      }

      const centavos = paraCentavos(l.valor);
      if (!centavos) problemas.push(`valor "${l.valor}" não é um número`);
      else if (BigInt(centavos) <= 0n) problemas.push("valor precisa ser maior que zero");

      const comp = lerCompetencia(l.competencia, exercicio);
      if (!comp) problemas.push(`competência "${l.competencia}" não foi entendida`);
      else if (Number(comp.slice(0, 4)) !== exercicio)
        problemas.push(`competência ${comp.slice(0, 7)} é de outro exercício`);

      const tipo = (l.tipo || "pontual").trim().toLowerCase();
      if (!TIPOS.includes(tipo)) problemas.push(`tipo "${l.tipo}" inválido`);

      return {
        origem: l,
        // FOPAG é um agregado de várias pessoas e, portanto, não tem uma linha
        // individual para exibir. CSV e MOVIMENTACOES mantêm sua linha de origem.
        numero: "linha_xlsx" in l ? (l.linha_xlsx ?? null) : i + 2,
        centroCustoCodigo: cc || null,
        categoriaId: categoria?.id ?? null,
        categoriaCodigo: categoria?.codigo ?? (codigoBruto || null),
        categoriaNome: categoria?.nome ?? (catBruta || null),
        subcategoria: (l.subcategoria ?? "").trim(),
        tipo,
        centavos,
        competencia: comp,
        problemas,
        aviso: undefined,
      };
    });
  }, [linhasLidas, cadastros.data, exercicio, nomesAmbiguos]);

  const boas = conferidas.filter((c) => c.problemas.length === 0);
  const ruins = conferidas.filter((c) => c.problemas.length > 0);
  const comAviso = boas.filter((c) => c.aviso);
  const total = boas.reduce((s, c) => s + BigInt(c.centavos ?? "0"), 0n).toString();

  const exercicioAtual = cadastros.data?.exercicios.find((e) => e.ano === exercicio);
  const exercicioEncerrado = exercicioAtual?.status === "encerrado";
  const exercicioExiste = !!exercicioAtual;

  const planilhaAtual = useQuery({
    queryKey: ["planilha", exercicio],
    queryFn: () => api.get<{ linhas: LinhaExistente[] }>(`/api/planilha?ano=${exercicio}`),
    enabled: boas.length > 0 && exercicioExiste,
  });

  const montadas = useMemo(() => montarLinhas(boas), [boas]);
  const planoAtualizacao = useMemo(() => planejarAtualizacaoEmMassa(
    montadas,
    planilhaAtual.data?.linhas ?? [],
    new Map((cadastros.data?.centros ?? []).map((c) => [c.codigo, c.id])),
  ), [montadas, planilhaAtual.data, cadastros.data]);
  const ocupacao = useMemo(() => {
    const existentes = new Map<string, bigint>();
    for (const linha of planilhaAtual.data?.linhas ?? []) {
      const chave = chaveCoordenada(linha.centro_custo_codigo, linha.categoria_id, linha.competencia);
      existentes.set(chave, (existentes.get(chave) ?? 0n) + BigInt(linha.valor_centavos));
    }
    const novas: LinhaMontada[] = [];
    const iguais: LinhaMontada[] = [];
    const conflitos: LinhaMontada[] = [];
    for (const linha of montadas) {
      const atual = existentes.get(chaveCoordenada(
        linha.centro_custo_codigo, linha.categoria_id, linha.competencia,
      ));
      if (atual === undefined) novas.push(linha);
      else if (atual === BigInt(linha.valor_centavos)) iguais.push(linha);
      else conflitos.push(linha);
    }
    return { novas, iguais, conflitos };
  }, [montadas, planilhaAtual.data]);

  const totalNovo = ocupacao.novas.reduce((s, l) => s + BigInt(l.valor_centavos), 0n).toString();
  const chavesIguais = new Set(ocupacao.iguais.map((l) =>
    chaveCoordenada(l.centro_custo_codigo, l.categoria_id, l.competencia)));
  const chavesConflito = new Set(ocupacao.conflitos.map((l) =>
    chaveCoordenada(l.centro_custo_codigo, l.categoria_id, l.competencia)));

  const importar = useMutation({
    mutationFn: () =>
      api.post<Resultado>("/api/orcamento/importar", {
        exercicio_ano: exercicio,
        linhas: ocupacao.novas,
        ignorar_ocupadas: true,
      }),
    onSuccess: (r) => {
      setResultado(r);
      void qc.invalidateQueries({ queryKey: ["execucao-linhas"] });
      void qc.invalidateQueries({ queryKey: ["visao-financeiro"] });
      void qc.invalidateQueries({ queryKey: ["painel-gestor"] });
      void qc.invalidateQueries({ queryKey: ["planilha", exercicio] });
    },
  });

  const atualizar = useMutation({
    mutationFn: async () => {
      // Uma aba antiga não pode sobrescrever uma edição feita depois da prévia.
      const agora = await api.get<{ linhas: LinhaExistente[] }>(`/api/planilha?ano=${exercicio}`);
      const planoAgora = planejarAtualizacaoEmMassa(
        montadas, agora.linhas,
        new Map((cadastros.data?.centros ?? []).map((c) => [c.codigo, c.id])),
      );
      if (JSON.stringify(planoAgora) !== JSON.stringify(planoAtualizacao)) {
        await qc.invalidateQueries({ queryKey: ["planilha", exercicio] });
        throw new Error("O orçamento mudou depois da prévia. Confira os valores atualizados antes de salvar.");
      }
      return api.post<{ alteradas: number; aviso?: string | null }>("/api/planilha", {
        exercicio_ano: exercicio,
        justificativa: justificativa.trim() || null,
        alteracoes: planoAgora.alteracoes,
      });
    },
    onSuccess: (r) => {
      setResultadoAtualizacao(r);
      void qc.invalidateQueries();
    },
  });

  async function escolherArquivo(arquivo: File): Promise<void> {
    setResultado(null);
    setResultadoAtualizacao(null);
    setErroArquivo(null);
    if (/\.xlsx$/i.test(arquivo.name)) {
      setLendoArquivo(true);
      try {
        const lido = await lerOrcamentoXlsx(arquivo);
        setXlsx(lido);
        setTexto("");
      } catch {
        setXlsx(null);
        setErroArquivo("Não consegui ler este .xlsx. Confirme se ele abre normalmente no Excel e tente de novo.");
      } finally {
        setLendoArquivo(false);
      }
      return;
    }
    setXlsx(null);
    setTexto(decodificarArquivo(await arquivo.arrayBuffer()));
  }

  return (
    <div className="pagina pb-20">
      <header className="py-7">
        <p className="text-[11px] font-semibold tracking-[0.11em] text-tinta-fraca uppercase">
          Orçamento
        </p>
        <h1 className="mt-1 font-display text-[22px] leading-tight font-bold tracking-tight">
          Importar ou atualizar orçamento em volume
        </h1>
        <p className="mt-0.5 max-w-3xl text-[13.5px] text-tinta-media">
          Escolha o arquivo anual do Financeiro em .xlsx. O sistema lê MOVIMENTACOES e FOPAG,
          separa o que é novo. Para trocar valores existentes, escolha o modo de atualização e confira a prévia.
        </p>
      </header>

      <div className="grid gap-4 lg:grid-cols-[1fr_360px]">
        <Painel>
          <fieldset className="mb-4 grid gap-2">
            <legend className="mb-1 text-[12px] font-semibold text-tinta-media">O que fazer com o arquivo</legend>
            <label className="flex cursor-pointer items-center gap-2 text-[12.5px]">
              <input type="radio" name="modo-importacao" checked={modo === "novos"}
                onChange={() => { setModo("novos"); setResultadoAtualizacao(null); }} />
              Importar somente valores novos
            </label>
            <label className="flex cursor-pointer items-center gap-2 text-[12.5px]">
              <input type="radio" name="modo-importacao" checked={modo === "atualizar"}
                onChange={() => { setModo("atualizar"); setResultado(null); }} />
              Atualizar valores existentes e incluir novos
            </label>
          </fieldset>
          <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
            <h2 className="text-[13.5px] font-bold">A planilha</h2>
            <label className="flex items-center gap-2 text-[12.5px]">
              <span className="font-semibold text-tinta-media">Exercício</span>
              <input
                type="number"
                min={2020}
                max={2099}
                value={exercicio}
                onChange={(e) => setExercicio(Number(e.target.value))}
                className="num w-24 rounded-lg border border-linha bg-superficie px-2 py-1 text-right"
              />
            </label>
          </div>

          {xlsx ? (
            <div className="rounded-lg border border-ok-linha bg-ok-bg px-4 py-3">
              <p className="text-[13px] font-bold text-ok-tinta">{xlsx.nomeArquivo}</p>
              <p className="mt-1 text-[12px] text-tinta-media">
                Arquivo lido. A prévia contém somente valores agregados; nomes e salários
                individuais da FOPAG não são enviados nem exibidos.
              </p>
            </div>
          ) : (
            <textarea
              value={texto}
              onChange={(e) => {
                setTexto(e.target.value); setXlsx(null); setErroArquivo(null); setResultado(null); setResultadoAtualizacao(null);
              }}
              rows={10}
              spellCheck={false}
              placeholder={exemploColado(exercicio)}
              className="w-full resize-y rounded-lg border border-linha bg-superficie px-3 py-2 font-mono text-[12.5px] leading-relaxed"
            />
          )}

          <div className="mt-2 flex flex-wrap items-center gap-3">
            <label className="cursor-pointer rounded-lg border border-linha px-3 py-1.5 text-[12.5px] font-semibold hover:border-laranja hover:text-laranja-tinta">
              {lendoArquivo ? "Lendo arquivo…" : "Escolher arquivo (.xlsx ou CSV)"}
              <input
                type="file"
                accept=".xlsx,.csv,.tsv,.txt"
                className="hidden"
                disabled={lendoArquivo}
                onChange={async (e) => {
                  const f = e.target.files?.[0];
                  if (f) await escolherArquivo(f);
                  // Sem isto, escolher o mesmo arquivo de novo (depois de corrigir) não dispara nada.
                  e.target.value = "";
                }}
              />
            </label>
            <button
              type="button"
              onClick={() => baixarModelo(exercicio)}
              className="rounded border border-linha px-3 py-1.5 text-[12.5px] font-semibold hover:border-laranja hover:text-laranja-tinta"
            >
              Baixar modelo CSV
            </button>
            {(texto || xlsx) && (
              <button
                type="button"
                onClick={() => {
                  setTexto(""); setXlsx(null); setErroArquivo(null); setResultado(null); setResultadoAtualizacao(null);
                }}
                className="text-[12.5px] text-tinta-fraca hover:text-tinta"
              >
                Limpar
              </button>
            )}
            <span className="ml-auto text-[12px] text-tinta-fraca">
              Use o modelo CSV desta tela ou o arquivo anual com PLANO_CONTAS, MOVIMENTACOES e FOPAG.
              O CSV exportado da grade Planilha tem outro formato.
            </span>
          </div>

          {erroArquivo && (
            <p className="mt-3 rounded-lg border border-risco-linha bg-risco-bg px-3 py-2 text-[12.5px] text-risco-tinta">
              {erroArquivo}
            </p>
          )}
          {leituraTexto?.erro && (
            <p className="mt-3 rounded-lg border border-risco-linha bg-risco-bg px-3 py-2 text-[12.5px] text-risco-tinta">
              {leituraTexto.erro}
            </p>
          )}
          {!!xlsx?.erros.length && (
            <ul className="mt-3 rounded-lg border border-risco-linha bg-risco-bg px-5 py-2 text-[12.5px] text-risco-tinta">
              {xlsx.erros.map((erro) => <li key={erro}>{erro}</li>)}
            </ul>
          )}
        </Painel>

        {/* ── resumo da conferência ─────────────────────────────────────── */}
        <Painel>
          <h2 className="mb-3 text-[13.5px] font-bold">Conferência</h2>

          {conferidas.length === 0 ? (
            <Vazio
              titulo="Nada conferido ainda"
              dica="Escolha o .xlsx ao lado e o resultado aparece aqui, antes de qualquer gravação."
            />
          ) : (
            <>
              {xlsx && (
                <div className="mb-3 grid gap-2 sm:grid-cols-2 lg:grid-cols-1">
                  <ResumoAba
                    nome="Movimentações"
                    detalhe={`${xlsx.movimentacoes.grupos} valores mensais · ${xlsx.movimentacoes.ignoradas} linhas sem valor`}
                    ok={xlsx.movimentacoes.encontrada}
                  />
                  <ResumoAba
                    nome="Folha de pagamento"
                    detalhe={`${xlsx.fopag.grupos} grupos PJ · ${xlsx.fopag.pessoasSemConta} linhas sem conta bloqueadas`}
                    ok={xlsx.fopag.encontrada}
                    aviso={xlsx.fopag.aviso}
                  />
                </div>
              )}
              {modo === "novos" ? <>
              <dl className="grid gap-2">
                <Linha rotulo="Linhas lidas" valor={String(conferidas.length)} />
                <Linha rotulo="Novas para importar" valor={String(ocupacao.novas.length)} tom="ok" />
                {ocupacao.iguais.length > 0 && (
                  <Linha rotulo="Já existem com o mesmo total" valor={String(ocupacao.iguais.length)} />
                )}
                {ocupacao.conflitos.length > 0 && (
                  <Linha rotulo="Conflitos mantidos como estão" valor={String(ocupacao.conflitos.length)} tom="atencao" />
                )}
                {ruins.length > 0 && (
                  <Linha rotulo="Com problema" valor={String(ruins.length)} tom="risco" />
                )}
                {comAviso.length > 0 && (
                  <Linha rotulo="Com aviso" valor={String(comAviso.length)} tom="atencao" />
                )}
                <Linha rotulo="Total lido sem erros" valor={moeda(total)} />
                <Linha rotulo="Total novo a importar" valor={moeda(totalNovo)} />
              </dl>

              {planilhaAtual.isFetching && (
                <p className="mt-3 text-[12px] text-tinta-fraca">Comparando com o orçamento atual…</p>
              )}
              {planilhaAtual.isError && (
                <p className="mt-3 rounded-lg border border-risco-linha bg-risco-bg px-3 py-2 text-[12.5px] text-risco-tinta">
                  Não foi possível conferir o que já existe. A importação fica bloqueada para não duplicar valores.
                </p>
              )}
              {ocupacao.conflitos.length > 0 && (
                <p className="mt-3 rounded-lg border border-atencao-linha bg-atencao-bg px-3 py-2 text-[12.5px] text-atencao-tinta">
                  {ocupacao.conflitos.length} centro(s) de custo + categoria + mês já têm outro valor.
                  Eles serão mantidos. Para substituir um valor existente, use a aba Planilha.
                </p>
              )}

              {!exercicioExiste && (
                <p className="mt-3 rounded-lg border border-risco-linha bg-risco-bg px-3 py-2 text-[12.5px] text-risco-tinta">
                  O exercício {exercicio} não existe no sistema. Ele precisa ser aberto antes da
                  importação.
                </p>
              )}
              {exercicioEncerrado && (
                <p className="mt-3 rounded-lg border border-risco-linha bg-risco-bg px-3 py-2 text-[12.5px] text-risco-tinta">
                  O exercício {exercicio} está encerrado.
                </p>
              )}

              {importar.isError && (
                <p className="mt-3 rounded-lg border border-risco-linha bg-risco-bg px-3 py-2 text-[12.5px] text-risco-tinta">
                  {(importar.error as FalhaApi).message}
                </p>
              )}

              <button
                type="button"
                disabled={
                  ocupacao.novas.length === 0 || !exercicioExiste || exercicioEncerrado
                  || importar.isPending || planilhaAtual.isFetching || planilhaAtual.isError
                  || !!xlsx?.erros.length
                }
                onClick={() => importar.mutate()}
                className="mt-4 w-full rounded-lg bg-laranja px-4 py-2 text-[13px] font-bold text-azul disabled:cursor-not-allowed disabled:opacity-45"
              >
                {importar.isPending
                  ? "Importando…"
                  : `Importar ${ocupacao.novas.length} ${ocupacao.novas.length === 1 ? "valor novo" : "valores novos"}`}
              </button>

              {ruins.length > 0 && (
                <p className="mt-2 text-center text-[11.5px] text-tinta-fraca">
                  As {ruins.length} com problema ficam de fora. Corrija a planilha e cole de novo.
                </p>
              )}
              </> : <>
                <dl className="grid gap-2">
                  <Linha rotulo="Linhas lidas" valor={String(conferidas.length)} />
                  <Linha rotulo="Coordenadas a alterar ou criar" valor={String(planoAtualizacao.previa.length)} tom="ok" />
                  <Linha rotulo="Células a salvar" valor={String(planoAtualizacao.alteracoes.length)} />
                  <Linha rotulo="Conflitos" valor={String(planoAtualizacao.conflitos.length)} tom={planoAtualizacao.conflitos.length ? "risco" : undefined} />
                  <Linha rotulo="Linhas inválidas" valor={String(ruins.length)} tom={ruins.length ? "risco" : undefined} />
                </dl>
                <p className="mt-3 text-[12px] text-tinta-media">
                  Somente as coordenadas do arquivo mudam. As demais ficam como estão; valores existentes são substituídos, nunca somados.
                </p>
                {planilhaAtual.isFetching && <p className="mt-3 text-[12px] text-tinta-fraca">Comparando com o orçamento atual…</p>}
                {planilhaAtual.isError && <p className="mt-3 text-[12px] text-risco-tinta">Não foi possível carregar o orçamento atual.</p>}
                {planoAtualizacao.conflitos.length > 0 && (
                  <ul className="thin-scroll mt-3 max-h-40 overflow-y-auto rounded border border-risco-linha bg-risco-bg px-3 py-2 text-[12px] text-risco-tinta">
                    {planoAtualizacao.conflitos.map((c, i) => <li key={i}>{c}</li>)}
                  </ul>
                )}
                {planoAtualizacao.previa.length > 0 && (
                  <div className="thin-scroll mt-3 max-h-56 overflow-y-auto rounded border border-linha">
                    <table className="w-full text-[11.5px]">
                      <thead className="sticky top-0 bg-superficie-2 text-tinta-media">
                        <tr><th className="px-2 py-1 text-left">CC · categoria · mês</th><th className="px-2 py-1 text-right">Antes</th><th className="px-2 py-1 text-right">Depois</th></tr>
                      </thead>
                      <tbody>{planoAtualizacao.previa.map((p) => (
                        <tr key={p.rotulo} className="border-t border-linha">
                          <td className="px-2 py-1">{p.rotulo}{p.nova ? " · nova" : ""}</td>
                          <td className="num px-2 py-1 text-right">{moeda(p.antes)}</td>
                          <td className="num px-2 py-1 text-right font-semibold">{moeda(p.depois)}</td>
                        </tr>
                      ))}</tbody>
                    </table>
                  </div>
                )}
                {exercicioAtual?.status === "vigente" && (
                  <label className="mt-3 grid gap-1 text-[12px] font-semibold text-tinta-media">
                    Justificativa da revisão do orçamento vigente
                    <textarea value={justificativa} onChange={(e) => setJustificativa(e.target.value)} rows={3}
                      placeholder="Explique por que os valores serão alterados. A justificativa fica no Histórico."
                      className="w-full rounded-lg border border-linha bg-superficie px-3 py-2 text-[12.5px] font-normal" />
                  </label>
                )}
                {!exercicioExiste && <p className="mt-3 text-[12px] text-risco-tinta">O exercício {exercicio} precisa ser aberto antes da atualização.</p>}
                {exercicioEncerrado && <p className="mt-3 text-[12px] text-risco-tinta">O exercício {exercicio} está encerrado.</p>}
                {atualizar.isError && <p className="mt-3 text-[12px] text-risco-tinta">{(atualizar.error as FalhaApi).message}</p>}
                <button type="button" onClick={() => atualizar.mutate()}
                  disabled={planoAtualizacao.alteracoes.length === 0 || planoAtualizacao.conflitos.length > 0 || ruins.length > 0
                    || !exercicioExiste || exercicioEncerrado || planilhaAtual.isFetching || planilhaAtual.isError
                    || !!xlsx?.erros.length || atualizar.isPending
                    || (exercicioAtual?.status === "vigente" && justificativa.trim().length < 10)}
                  className="mt-4 w-full rounded-lg bg-laranja px-4 py-2 text-[13px] font-bold text-azul disabled:cursor-not-allowed disabled:opacity-45">
                  {atualizar.isPending ? "Salvando…" : `Salvar ${planoAtualizacao.alteracoes.length} ${planoAtualizacao.alteracoes.length === 1 ? "célula" : "células"} no orçamento`}
                </button>
                {(ruins.length > 0 || planoAtualizacao.conflitos.length > 0) && (
                  <p className="mt-2 text-center text-[11.5px] text-risco-tinta">Corrija todos os problemas para salvar o arquivo inteiro.</p>
                )}
              </>}
            </>
          )}
        </Painel>
      </div>

      {/* ── resultado da importação ───────────────────────────────────── */}
      {resultado && (
        <Painel className="mt-4">
          <h2 className="mb-3 text-[13.5px] font-bold">Resultado da importação</h2>
          <div className="flex flex-wrap gap-2">
            <Pilula tom="ok">{resultado.aceitas} aceitas</Pilula>
            {resultado.rejeitadas > 0 && (
              <Pilula tom="risco">{resultado.rejeitadas} rejeitadas</Pilula>
            )}
            {resultado.sem_correspondencia > 0 && (
              <Pilula tom="atencao">{resultado.sem_correspondencia} sem correspondência</Pilula>
            )}
            {!!resultado.repetidas && (
              <Pilula tom="atencao">{resultado.repetidas} onde já havia orçamento</Pilula>
            )}
            {!!resultado.ignoradas_ocupadas && (
              <Pilula tom="atencao">{resultado.ignoradas_ocupadas} já existentes foram preservadas</Pilula>
            )}
          </div>

          {/* ⚠️ Nada disto foi recusado: a linha entrou AO LADO da que já
              existia, e o orçado daquele mês passou a ser a soma das duas. É o
              que acontece quando se reenvia a planilha inteira depois de
              corrigir uma parte — o caminho natural de quem tem a planilha na
              mão. Ver a migration 042. */}
          {!!resultado.repetidas && (
            <p className="mt-3 rounded-lg border border-atencao-linha bg-atencao-bg px-3 py-2 text-[12.5px] text-atencao-tinta">
              <strong>{resultado.repetidas}</strong>{" "}
              {resultado.repetidas === 1 ? "linha entrou" : "linhas entraram"} num centro de custo,
              categoria e mês que <strong>já tinham orçamento</strong>. Elas não substituíram nada:
              o orçado desses meses passou a ser a soma. Se a intenção era corrigir um valor, o
              caminho é a aba Planilha — e o que entrou aqui precisa ser retirado.
            </p>
          )}
          {!!resultado.ignoradas_ocupadas && (
            <p className="mt-3 rounded-lg border border-atencao-linha bg-atencao-bg px-3 py-2 text-[12.5px] text-atencao-tinta">
              Entre a prévia e a gravação, {resultado.ignoradas_ocupadas}{" "}
              {resultado.ignoradas_ocupadas === 1 ? "coordenada já estava" : "coordenadas já estavam"}{" "}
              ocupada(s). O sistema preservou o orçamento atual e não somou os valores do arquivo.
            </p>
          )}
          {resultado.detalhes.length > 0 && (
            <ul className="thin-scroll mt-3 max-h-64 overflow-y-auto">
              {resultado.detalhes.map((d, i) => (
                <li key={i} className="border-t border-linha py-1.5 text-[12.5px] first:border-t-0">
                  <span className="font-semibold text-risco-tinta">{d.resultado}</span> — {d.motivo}
                </li>
              ))}
            </ul>
          )}

          {/* Identificadas pela COORDENADA, não pelo número da linha: o payload
              é reordenado ao agrupar as subcategorias, então o índice que volta
              da RPC não é a linha da planilha. Dizer "linha 12" mandaria a
              pessoa olhar o lugar errado. */}
          {!!resultado.avisos?.some((a) => a.coordenada_ocupada) && (
            <ul className="thin-scroll mt-3 max-h-56 overflow-y-auto">
              {resultado.avisos
                .filter((a) => a.coordenada_ocupada)
                .map((a, i) => (
                  <li key={i} className="border-t border-linha py-1.5 text-[12.5px] first:border-t-0">
                    <span className="num font-semibold">{a.linha?.centro_custo_codigo ?? "—"}</span>
                    {" · "}
                    {a.linha?.categoria_nome ?? "—"}
                    {" · "}
                    <span className="num">{a.linha?.competencia?.slice(0, 7) ?? "—"}</span>
                    {" — "}
                    <span className="text-atencao-tinta">
                      {a.coordenada_ocupada === "lote"
                        ? "repetida dentro da própria planilha"
                        : "já havia orçamento aqui"}
                    </span>
                  </li>
                ))}
            </ul>
          )}
          <p className="mt-3 text-[12px] text-tinta-fraca">
            A importação inteira ficou registrada no Histórico de Movimentações.
          </p>
        </Painel>
      )}

      {resultadoAtualizacao && (
        <Painel className="mt-4">
          <h2 className="text-[13.5px] font-bold">Atualização concluída</h2>
          <p className="mt-1 text-[12.5px] text-tinta-media">
            {resultadoAtualizacao.alteradas} {resultadoAtualizacao.alteradas === 1 ? "célula salva" : "células salvas"}.
            A revisão e a justificativa ficaram no Histórico de Movimentações.
          </p>
          {resultadoAtualizacao.aviso && <p className="mt-2 text-[12px] text-atencao-tinta">{resultadoAtualizacao.aviso}</p>}
        </Painel>
      )}

      {/* ── linhas conferidas ─────────────────────────────────────────── */}
      {conferidas.length > 0 && (
        <Painel className="mt-4">
          <h2 className="mb-3 text-[13.5px] font-bold">Linha a linha</h2>
          <div className="thin-scroll max-h-[520px] overflow-auto">
            <table className="w-full min-w-[820px] border-collapse text-[13px]">
              <thead className="sticky top-0 bg-superficie">
                <tr className="text-[11px] tracking-wide text-tinta-fraca uppercase">
                  <th className="border-b-2 border-azul px-2 py-2 text-right font-semibold">#</th>
                  <th className="border-b-2 border-azul px-2 py-2 text-left font-semibold">Origem</th>
                  <th className="border-b-2 border-azul px-2 py-2 text-left font-semibold">CC</th>
                  <th className="border-b-2 border-azul px-2 py-2 text-left font-semibold">Categoria</th>
                  <th className="border-b-2 border-azul px-2 py-2 text-left font-semibold">Subcategoria</th>
                  <th className="border-b-2 border-azul px-2 py-2 text-left font-semibold">Tipo</th>
                  <th className="border-b-2 border-azul px-2 py-2 text-right font-semibold">Valor</th>
                  <th className="border-b-2 border-azul px-2 py-2 text-left font-semibold">Competência</th>
                  <th className="border-b-2 border-azul px-2 py-2 text-left font-semibold">Situação</th>
                </tr>
              </thead>
              <tbody>
                {conferidas.map((c) => (
                  <tr
                    key={`${c.origem.origem_xlsx ?? "CSV"}|${c.numero}|${c.centroCustoCodigo}|${c.categoriaId}|${c.competencia}|${c.subcategoria}`}
                    className={c.problemas.length ? "bg-risco-bg/40" : undefined}
                  >
                    <td className="num border-t border-linha px-2 py-1.5 text-right text-tinta-fraca">
                      {c.numero ?? "—"}
                    </td>
                    <td className="border-t border-linha px-2 py-1.5 text-[11.5px] text-tinta-fraca">
                      {c.origem.origem_xlsx ?? "CSV"}
                    </td>
                    <td className="num border-t border-linha px-2 py-1.5">{c.centroCustoCodigo ?? "—"}</td>
                    <td className="border-t border-linha px-2 py-1.5">{c.categoriaNome ?? "—"}</td>
                    <td className="border-t border-linha px-2 py-1.5 text-tinta-media">{c.subcategoria || "—"}</td>
                    <td className="border-t border-linha px-2 py-1.5 text-tinta-media">{c.tipo}</td>
                    <td className="num border-t border-linha px-2 py-1.5 text-right">
                      {c.centavos ? moeda(c.centavos) : (c.origem.valor || "—")}
                    </td>
                    <td className="num border-t border-linha px-2 py-1.5">
                      {c.competencia ? c.competencia.slice(0, 7) : c.origem.competencia || "—"}
                    </td>
                    <td className="border-t border-linha px-2 py-1.5">
                      {c.problemas.length > 0 ? (
                        <span className="text-[12px] text-risco-tinta">{c.problemas.join("; ")}</span>
                      ) : modo === "atualizar" ? (
                        <span className="text-[12px] text-tinta-media">Conferir na prévia acima</span>
                      ) : chavesConflito.has(chaveCoordenada(c.centroCustoCodigo, c.categoriaId, c.competencia)) ? (
                        <Pilula tom="atencao">Conflito · mantido</Pilula>
                      ) : chavesIguais.has(chaveCoordenada(c.centroCustoCodigo, c.categoriaId, c.competencia)) ? (
                        <Pilula tom="neutro">Já existe</Pilula>
                      ) : c.aviso ? (
                        <span className="text-[12px] text-atencao-tinta">{c.aviso}</span>
                      ) : (
                        <Pilula tom="ok">Pronta</Pilula>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Painel>
      )}
    </div>
  );
}

function Linha({ rotulo, valor, tom }: { rotulo: string; valor: string; tom?: string }) {
  const cor =
    tom === "ok" ? "text-ok-tinta" : tom === "risco" ? "text-risco-tinta"
    : tom === "atencao" ? "text-atencao-tinta" : "text-tinta";
  return (
    <div className="flex items-baseline justify-between gap-3 border-b border-linha pb-1.5 last:border-b-0">
      <dt className="text-[13px] text-tinta-media">{rotulo}</dt>
      <dd className={`num text-[14px] font-bold ${cor}`}>{valor}</dd>
    </div>
  );
}

function ResumoAba({
  nome, detalhe, ok, aviso,
}: {
  nome: string;
  detalhe: string;
  ok: boolean;
  aviso?: string;
}) {
  return (
    <div className="rounded-lg border border-linha px-3 py-2">
      <div className="flex items-center justify-between gap-2">
        <strong className="text-[12.5px]">{nome}</strong>
        <Pilula tom={ok ? "ok" : "risco"}>{ok ? "Lida" : "Ausente"}</Pilula>
      </div>
      <p className="mt-1 text-[11.5px] text-tinta-fraca">{detalhe}</p>
      {aviso && <p className="mt-1 text-[11.5px] text-atencao-tinta">{aviso}</p>}
    </div>
  );
}
